<template>
  <div>
    <h4>
      <q-icon name="grid_view" /> Gérer la position des fichiers
    </h4>

    <q-list bordered separator>
      <q-item v-for="(row, idx) in rows" :key="row.id" class="resource-item">
        <q-item-section avatar>
          <div class="resource-preview">
            <img
              v-if="isImage(row.name) && previews[row.name]"
              :src="previews[row.name]"
              class="preview-thumb"
            />
            <div v-else-if="isImage(row.name)" class="preview-placeholder">
              <q-spinner size="20px" color="grey" />
            </div>
            <div v-else class="preview-placeholder">
              <q-icon
                :name="getFileIcon(row.name)"
                :color="getFileIconColor(row.name)"
                size="28px"
              />
            </div>
          </div>
        </q-item-section>

        <q-item-section>
          <q-item-label>
            <span class="text-weight-medium text-grey-7">{{ idx + 1 }}.</span>
            {{ row.title }}
          </q-item-label>
          <q-item-label caption>{{ row.name }}</q-item-label>
        </q-item-section>

        <q-item-section side>
          <div class="action-icons">
            <q-btn flat round icon="arrow_upward" size="sm" :disable="idx === 0" @click="up(row)">
              <q-tooltip>Monter</q-tooltip>
            </q-btn>
            <q-btn flat round icon="arrow_downward" size="sm" :disable="idx === rows.length - 1" @click="down(row)">
              <q-tooltip>Descendre</q-tooltip>
            </q-btn>
          </div>
        </q-item-section>
      </q-item>

      <q-item v-if="rows.length === 0">
        <q-item-section class="text-grey text-center">
          Aucun fichier dans la galerie
        </q-item-section>
      </q-item>
    </q-list>

    <div class="q-mt-md">
      <q-btn label="Enregistrer" color="secondary" @click="saveAllIndices" />
    </div>
    <q-banner v-if="success" class="app-banner app-banner--success q-mt-sm" dense rounded>
      Données enregistrées
    </q-banner>
    <q-banner v-if="errorBanner" class="app-banner app-banner--error q-mt-sm" dense rounded>
      Erreur durant l'enregistrement des données
    </q-banner>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import axios from "axios";
import { useStore } from "vuex";

export default {
  props: {
    exhibitId: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props, ctx) {
    const store = useStore();
    const rows = ref([]);
    const key = ref(1);
    const success = ref(false);
    const errorBanner = ref(false);
    const currentExhibitId = computed(() => props.exhibitId ?? store.state.exhibitId);
    const modifiedRows = ref([]);
    const homeImages = ref([]);
    const previews = reactive({});

    const isImage = (fileName) => /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
    const isVideo = (fileName) => /\.(mp4|mov|avi|webm|mkv)$/i.test(fileName);
    const isAudio = (fileName) => /\.(mp3|wav|ogg|m4a)$/i.test(fileName);
    const isPDF = (fileName) => /\.pdf$/i.test(fileName);

    const getFileIcon = (fileName) => {
      if (isVideo(fileName)) return 'videocam';
      if (isAudio(fileName)) return 'audiotrack';
      if (isPDF(fileName)) return 'picture_as_pdf';
      return 'insert_drive_file';
    };

    const getFileIconColor = (fileName) => {
      if (isVideo(fileName)) return 'purple';
      if (isAudio(fileName)) return 'orange';
      if (isPDF(fileName)) return 'red';
      return 'grey';
    };

    const fetchPreview = async (name) => {
      if (previews[name] || !isImage(name)) return;
      try {
        const url = `${process.env.VUE_APP_BASEURL}/s3Resource/${name}`;
        const response = await axios.get(url);
        previews[name] = response.data;
      } catch (e) {
        console.log('Preview fetch error for', name);
      }
    };

    const fetchAllPreviews = () => {
      rows.value.forEach(row => {
        if (isImage(row.name)) fetchPreview(row.name);
      });
    };

    watch(rows, fetchAllPreviews);
    const start = async () => {
      await fetchHomeResources();
      await fetchResources();
    }

    const fetchResources = async () => {
      try {
        // Obtenir les ressources de l'exposition
        let url = `${process.env.VUE_APP_BASEURL}/resources`;
        let body = { exhibitId: currentExhibitId.value };
        let response = await axios.post(url, body);
        let allResources = response.data;
        // Obtenir les resource_id de gallery_resource
        let galleryUrl = `${process.env.VUE_APP_BASEURL}/galleryResources/${currentExhibitId.value}`;
        let galleryResponse = await axios.get(galleryUrl);
        let galleryResourceIds = galleryResponse.data.map(res => Number(res.resource_id));
        let homeResourceIds = homeImages.value.map(Number).filter(Number.isFinite);
        let galleryOrder = new Map(galleryResourceIds.map((resourceId, index) => [resourceId, index]));

        // Filtrer les ressources pour ne garder que celles présentes dans gallery_resource
        rows.value = allResources
          .filter(resource => galleryResourceIds.includes(Number(resource.id)))
          .filter(resource => !homeResourceIds.includes(Number(resource.id)) && !homeResourceIds.includes(Number(resource.resource_id)))
          .sort((a, b) => {
            const aIndex = Number.isFinite(Number(a.index)) ? Number(a.index) : galleryOrder.get(Number(a.id));
            const bIndex = Number.isFinite(Number(b.index)) ? Number(b.index) : galleryOrder.get(Number(b.id));
            return aIndex - bIndex;
          });

        // Ajouter toutes les rows à modifiedRows
        modifiedRows.value = [...rows.value];
        store.commit('SET_RESOURCESCOUNT', rows.value.length);
      } catch (error) {
        console.log("error :" + error);
      }
    }


    const fetchHomeResources = async () => {
      try {
        let url = `${process.env.VUE_APP_BASEURL}/exhibit_home/${currentExhibitId.value}`;
        let response = await axios.get(url);
        if (response.data?.[0]) {
          homeImages.value.push(response.data[0].resource_id);
          homeImages.value.push(response.data[0].launch_image_id);
        }

      } catch (error) {
        console.log(error);
      }
    }

    const up = (item) => {
      const index = rows.value.indexOf(item);
      if (index > 0) {
        const temp = rows.value[index - 1];
        rows.value.splice(index - 1, 1, item);
        rows.value.splice(index, 1, temp);
        // Mise à jour des index
        rows.value[index - 1].index = index - 1;
        rows.value[index].index = index;
      }

    }

    const down = (item) => {
      console.log("Down called for item:", item);

      const index = rows.value.indexOf(item);
      if (index < rows.value.length - 1) {
        const temp = rows.value[index + 1];
        rows.value.splice(index + 1, 1, item);
        rows.value.splice(index, 1, temp);
        // Mise à jour des index
        rows.value[index + 1].index = index + 1;
        rows.value[index].index = index;
      }

    }



    const saveAllIndices = async () => {
      try {
        let url = `${process.env.VUE_APP_BASEURL}/resourceIndices`;
        await axios.put(url, { modifiedRows: modifiedRows.value });
        modifiedRows.value = [];  // clear après avoir sauvegardé
        await fetchResources();
        success.value = true;
        setTimeout(() => {
          success.value = false;
          ctx.emit("indicesSaved")
        }, 4000)
      } catch (error) {
        console.log(error);
        errorBanner.value = true;
        setTimeout(() => {
          errorBanner.value = false;
        }, 4000)
      }
    }

    onMounted(start);

    return {
      currentExhibitId,
      rows,
      key,
      success,
      errorBanner,
      modifiedRows,
      homeImages,
      previews,
      isImage,
      getFileIcon,
      getFileIconColor,
      fetchResources,
      up,
      down,
      saveAllIndices
    }
  }
}
</script>




<style scoped>
.resource-item {
  padding: 8px 16px;
}

.resource-preview {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.preview-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 6px;
}

.action-icons {
  display: flex;
  gap: 2px;
}
</style>
